import { useState } from "react";
import { MapPin, Search } from "lucide-react";
import { BRAZILIAN_STATES, type EntregaDoPedido } from "@bookdental/schemas";
import { fetchAddressFromCep, formatCep } from "@/lib/viacep";
import { formatarEndereco } from "./endereco-de-entrega";

type Props = {
  /** Endereço escolhido só pra este pedido; `null` = usar o padrão. */
  valor: EntregaDoPedido | null;
  onChange: (v: EntregaDoPedido | null) => void;
  /** O que vale sem troca: o endereço da clínica (ou o que o pedido em edição já tem). */
  padrao: EntregaDoPedido | null;
};

const VAZIO: EntregaDoPedido = {
  destinatario: "",
  telefone: "",
  cep: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "SP",
};

export const entregaCompleta = (v: EntregaDoPedido) =>
  v.destinatario.trim().length >= 2 &&
  v.cep.replace(/\D/g, "").length === 8 &&
  v.endereco.trim().length >= 2 &&
  v.numero.trim().length >= 1 &&
  v.bairro.trim().length >= 2 &&
  v.cidade.trim().length >= 2 &&
  !!v.uf;

const campo =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm dark:border-border dark:bg-card";

/** Para onde o trabalho volta. Vem com o endereço da clínica; trocar vale só pra este pedido. */
export function EnderecoDeEnvio({ valor, onChange, padrao }: Props) {
  const [buscando, setBuscando] = useState(false);
  const editar = (parcial: Partial<EntregaDoPedido>) => onChange({ ...(valor ?? VAZIO), ...parcial });

  const buscarCep = async () => {
    const cep = (valor?.cep ?? "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setBuscando(true);
    const r = await fetchAddressFromCep(cep);
    setBuscando(false);
    if (!r) return;
    // Rua nova: número e complemento eram do endereço anterior (o da clínica, quase sempre).
    const outraRua = !!r.address && r.address !== valor?.endereco;
    editar({
      cep: r.zipCode,
      endereco: r.address || valor?.endereco || "",
      bairro: r.neighborhood || valor?.bairro || "",
      cidade: r.city || valor?.cidade || "",
      uf: (r.state || valor?.uf || "SP") as EntregaDoPedido["uf"],
      ...(outraRua ? { numero: "", complemento: "" } : {}),
    });
  };

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Endereço de envio</p>
            {!valor && (
              <p className="text-[12.5px] text-zinc-600 dark:text-muted-foreground">
                {padrao ? formatarEndereco(padrao) : "A clínica ainda não tem endereço cadastrado."}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(valor ? null : { ...VAZIO, ...(padrao ?? {}), complemento: padrao?.complemento ?? "", telefone: padrao?.telefone ?? "" })}
          className="text-[12.5px] font-medium text-brand-700 hover:underline dark:text-primary"
        >
          {valor ? "Usar o endereço da clínica" : "Mudar o endereço de envio?"}
        </button>
      </div>

      {valor && (
        <div className="mt-3 grid gap-2.5 sm:grid-cols-12">
          <input aria-label="Destinatário" placeholder="Destinatário" value={valor.destinatario} onChange={(e) => editar({ destinatario: e.target.value })} className={`${campo} sm:col-span-8`} />
          <input aria-label="Telefone" placeholder="Telefone" value={valor.telefone ?? ""} onChange={(e) => editar({ telefone: e.target.value })} className={`${campo} sm:col-span-4`} />
          <div className="flex gap-2 sm:col-span-4">
            <input aria-label="CEP" placeholder="CEP" inputMode="numeric" value={formatCep(valor.cep)} onChange={(e) => editar({ cep: e.target.value.replace(/\D/g, "").slice(0, 8) })} onBlur={buscarCep} className={campo} />
            <button type="button" onClick={buscarCep} disabled={buscando} aria-label="Buscar CEP" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 dark:border-border">
              <Search className="h-4 w-4" />
            </button>
          </div>
          <input aria-label="Logradouro" placeholder="Logradouro" value={valor.endereco} onChange={(e) => editar({ endereco: e.target.value })} className={`${campo} sm:col-span-6`} />
          <input aria-label="Número" placeholder="Número" value={valor.numero} onChange={(e) => editar({ numero: e.target.value })} className={`${campo} sm:col-span-2`} />
          <input aria-label="Complemento" placeholder="Complemento (opcional)" value={valor.complemento ?? ""} onChange={(e) => editar({ complemento: e.target.value })} className={`${campo} sm:col-span-4`} />
          <input aria-label="Bairro" placeholder="Bairro" value={valor.bairro} onChange={(e) => editar({ bairro: e.target.value })} className={`${campo} sm:col-span-3`} />
          <input aria-label="Cidade" placeholder="Cidade" value={valor.cidade} onChange={(e) => editar({ cidade: e.target.value })} className={`${campo} sm:col-span-3`} />
          <select aria-label="UF" value={valor.uf} onChange={(e) => editar({ uf: e.target.value as EntregaDoPedido["uf"] })} className={`${campo} sm:col-span-2`}>
            {BRAZILIAN_STATES.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
          <p className="text-[11.5px] text-zinc-500 sm:col-span-12 dark:text-muted-foreground">
            Vale só para este pedido — o endereço da clínica continua o mesmo.
          </p>
        </div>
      )}
    </section>
  );
}
